import React, {Dispatch, SetStateAction, useCallback, useEffect} from "react";
import _ from "lodash";
import {
	Alignment,
	Fit,
	Layout,
	Rive,
	useRive,
	useViewModelInstanceArtboard,
} from "@rive-app/react-canvas-lite";
import {User, PlacementTestInfo} from "shared/types/VaultTypes";
import AssetsUtil from "../../util/AssetsUtil";
import {AT} from "../../vault/ActionTypeConstants";

const THROTTLE_MS = 500;

export const LOOK_DOWN_TRIGGER = "lookDown";
export const LOOK_UP_TRIGGER = "lookUp";
export const FLY_AWAY_TRIGGER = "flyAway";
const OPEN_DOOR_TRIGGER = "openDoor";

export type OnboardingStep =
	| "NOT_STARTED"
	| "IN_PROGRESS"
	| "PLACEMENT_TEST_LOAD_ERROR"
	| "CHOOSE_A_LEVEL"
	| "TEST_COMPLETE"
	| "DOOR_OPENING_ANIMATION"
	| "READY_TO_START"
	| "PARENT_MANUALLY_SELECTED_LEVEL";

export type OnboardingState = {
	step: OnboardingStep;
	manualLevelSelection?: {
		from: OnboardingStep;
		level: string;
		chapterIndex: number;
	};
};

const PROD_ONE_PLACEMENT_URL =
	"https://artofproblemsolving.com/placement/virtual";
const DEV_ONE_PLACEMENT_URL =
	"https://web-placement-git-shiny-aops-ba.vercel.app/virtual";
export const getOnePlacementUrl = (user: User) => {
	const baseUrl =
		process.env.NODE_ENV === "production"
			? PROD_ONE_PLACEMENT_URL
			: DEV_ONE_PLACEMENT_URL;
	const urlBase = `${baseUrl}?source=BA`;
	if (user.model?.gradeID) {
		return `${urlBase}&grade=${user.model.gradeID}`;
	}
	return urlBase;
};
type ChapterLetter = "A" | "B" | "C" | "D";
const getChapterIndexFromLetter = (chapterLetterResult: ChapterLetter) => {
	// 0-based index, the actual chapters from the letters are A -> (1, 2, 3), B -> (4, 5, 6), C -> (7, 8, 9), D -> (10, 11, 12)
	// Only return the first index from those chapters as the "start" point.
	return {
		A: 0,
		B: 3,
		C: 6,
		D: 9,
	}[chapterLetterResult];
};
export const getPlacementLevelAndChapter = (
	placementTestInfo: PlacementTestInfo | undefined,
	user: User
) => {
	if (!placementTestInfo) {
		// If placementTestInfo has not loaded successfully, default to Grade 3, Chapter 1
		return {
			level: user.model?.gradeID ?? "3",
			chapterIndex: user.model?.placementChapterIndex ?? 0,
		};
	}
	let previousResult = "1A";
	if (
		placementTestInfo.previousResults?.results &&
		placementTestInfo.previousResults.results.length > 0
	) {
		previousResult = placementTestInfo.previousResults.results[0].placement;
	}

	// If user placed out of BA but still went back here, we can only (at max) allow them to be on grade 5!!
	if (parseInt(previousResult[0]) > 5) {
		return {
			level: "5",
			chapterIndex: getChapterIndexFromLetter("D"),
		};
	}
	// Previous result, if it exists, will always be in the format of `${GradeNumber}${Letter}`.
	return {
		level: previousResult[0],
		chapterIndex: getChapterIndexFromLetter(previousResult[1] as ChapterLetter),
	};
};

export const useSchoolBackgroundAnimation = ({
	containerRef,
}: {
	containerRef: React.MutableRefObject<HTMLDivElement | null>;
}) => {
	const {rive, RiveComponent, canvas} = useRive({
		src: AssetsUtil.getRiveAnimationFileFromS3("student_onboarding_v3"),
		stateMachines: "State Machine 1",
		layout: new Layout({
			fit: Fit.Cover,
			alignment: Alignment.Center,
		}),
		autoplay: true,
		autoBind: true,
	});
	const viewModelInstance = rive?.viewModelInstance;
	const {setValue: setRiveLevelPlaque} = useViewModelInstanceArtboard(
		"plaqueLevel",
		viewModelInstance
	);
	// TODO(jaketrower): I'm really not sure why I have to do this manual resizing.
	// It seems like `SchoolBackground.tsx` used in the parent onboarding flow doesn't have to do this.
	// And the resizing still works. But the useRive hook components are near identical.
	const onResizeCanvasContainer = useCallback(() => {
		if (canvas && containerRef.current && rive) {
			canvas.width = containerRef.current.clientWidth;
			canvas.height = containerRef.current.clientHeight;
			canvas.style.width = `${containerRef.current.clientWidth}px`;
			canvas.style.height = `${containerRef.current.clientHeight}px`;
			rive.resizeToCanvas();
		}
	}, [canvas, containerRef, rive]);
	const throttledResizeCanvasContainer = _.throttle(
		onResizeCanvasContainer,
		THROTTLE_MS
	);
	useEffect(() => {
		window.addEventListener("resize", throttledResizeCanvasContainer);
		return () => {
			window.removeEventListener("resize", throttledResizeCanvasContainer);
		};
	}, [containerRef, throttledResizeCanvasContainer]);
	return {rive, RiveComponent, setRiveLevelPlaque};
};

export const riveAnimateDoorOpening = (
	rive: Rive | null,
	currentStep: OnboardingStep,
	setOnboardingState: Dispatch<SetStateAction<OnboardingState>>,
	selectedLevel?: number | undefined
) => {
	const manualLevelSelection =
		selectedLevel !== undefined
			? {
					from: currentStep,
					level: `${selectedLevel}`,
					chapterIndex: 0,
			  }
			: undefined;
	rive?.viewModelInstance?.trigger(OPEN_DOOR_TRIGGER)?.trigger();
	setOnboardingState({
		step: "DOOR_OPENING_ANIMATION",
		manualLevelSelection,
	});
	setTimeout(() => {
		setOnboardingState({
			step: "READY_TO_START",
			manualLevelSelection,
		});
	}, 2430);
};

export const getMixpanelDataFromUser = (user: User) => {
	return {
		studentID: user.userID,
		studentPlatformUserID: user.platformUserID,
		level: user.model?.gradeID,
	};
};

export const takePlacementTestFromOptIn = (
	user: User,
	dispatch: (action: unknown) => void
) => {
	// Instead of navigating directly to the URL, set a flag on our side
	// So that we know to "check" to see if the user finished the placement test.
	// (Otherwise, we might notify student on unrelated or old placement test results)
	dispatch({type: AT.FORCE_LOAD_USER_PENDING});
	dispatch({
		type: AT.UPDATE_USER_EVENTS_TRIGGERED,
		pathArray: ["notifications", "studentProfilePlacementTest"],
		propValue: new Date().toISOString(),
		callback: () => {
			if (global.window) {
				window.location.href = getOnePlacementUrl(user);
			}
		},
	});
};
